import { memo, useState } from 'react'
import { standardSizeFileUpload } from '../utils/uploadHelper'
import { Uploader } from './Uploader'
import styled from 'styled-components'
import { renderMuiFormField } from 'utils/forms/renderMuiFormField'
import {
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  DialogActions,
} from '@mui/material'
import HelpIcon from '@mui/icons-material/Help'
import { Fade } from '@mui/material'
import { selectData } from 'layouts/onboarding'
import Grid from '@mui/material/Grid'
import { Button } from '@mui/material'
import { useIntake } from 'hooks/useIntakeContext'
import { useFinances } from 'hooks/useFinances'
import ocrImage from 'assets/images/ocr-upload.svg'

const StyledDiv = styled.div`
  display: flex;
  flex-direction: row;
  > div.question {
    width: 70%;
  }
  > div.answer {
    width: 30%;
  }
  border-bottom: 1px solid #efefef;
`

const fakeQueries = [
  {
    question:
      'In the EARNINGS AND DEDUCTIONS table what is the AMOUNT P/P for NET PAY?',
    answer: '301.08',
    questionKey: 'netTakeHome',
  },
  {
    question:
      'In the EARNINGS AND DEDUCTIONS table what is the AMOUNT P/P for FEGLI- COVERAGE?',
    answer: '12.48',
    questionKey: 'fegliCoverage',
  },
  {
    question:
      'In the EARNINGS AND DEDUCTIONS table what is the AMOUNT P/P for VISION PLAN?',
    answer: '3.54',
    questionKey: 'deduction.fdvv',
  },
  {
    question:
      'In the EARNINGS AND DEDUCTIONS table what is the AMOUNT P/P for DENTAL PLAN?',
    answer: '16.53',
    questionKey: 'deduction.fdvd',
  },
  {
    question:
      'In the EARNINGS AND DEDUCTIONS table what is the AMOUNT P/P for TSP-FERS?',
    answer: '34.77',
    questionKey: 'tspContribution',
  },
  {
    question:
      'In the EARNINGS AND DEDUCTIONS table what is the AMOUNT P/P for FEHBA - ENROLL CODE?',
    answer: '18.57',
    questionKey: 'healthInsurancePremium',
  },
  {
    question: 'What is the FEHBA - ENROLL CODE?',
    answer: '111',
    questionKey: 'fehbCode',
  },
  {
    question: 'What is the SCD FOR LEAVE?',
    answer: '06/02/1991',
    questionKey: 'dateOfHire',
  },
  {
    question: 'What is the SALARY?',
    answer: '$75,582.00',
    questionKey: 'baseSalary',
  },
  {
    question:
      'What is the SICK BALANCE in the table YEAR TO DATE LEAVE STATUS?',
    answer: '-8.50',
    questionKey: 'currentSickLeaveBalance',
  },
  {
    question: 'What is the ANN BALANCE in the table YEAR TO DATE LEAVE STATUS?',
    answer: '127.00',
    questionKey: 'currentLeaveBalance',
  },
]

const questionsToInputs = {
  currentLeaveBalance: (answer) => ({
    autoFocus: false,
    id: 'currentLeaveBalance',
    label: 'Current Earned Annual Leave Balance',
    placeholder: '100',
    inputType: 'int',
    value: answer,
  }),
  currentSickLeaveBalance: (answer) => ({
    autoFocus: false,
    id: 'currentSickLeaveBalance',
    label: 'Current Sick Leave (Hours)',
    placeholder: '100',
    inputType: 'int',
    value: answer,
  }),
  baseSalary: (answer) => ({
    autoFocus: false,
    id: 'baseSalary',
    label: (
      <>
        <span>
          What is your Adjusted Base Annual Salary?
          <span style={{ color: 'red' }}> *</span>
          <Tooltip
            TransitionComponent={Fade}
            title="Your Adjusted base salary is your base salary including any locality pay you receive."
            placement="top"
            color="info"
            enterTouchDelay={0}
            leaveTouchDelay={3000}
          >
            <IconButton size="small" color="dark">
              <HelpIcon sx={{ marginTop: '-6px' }} />
            </IconButton>
          </Tooltip>
        </span>
      </>
    ),
    placeholder: '$62,000.00',
    inputType: 'currencyFloat',
    value: answer.replace('$', '').replace(',', ''),
  }),
  dateOfHire: (answer) => {
    const [month, day, year] = answer.split('/')
    return {
      autoFocus: false,
      id: 'dateOfHire',
      label: 'Service Computation Date',
      placeholder: '1970-01-01',
      inputType: 'date',
      value: [year, month, day].join('-'),
    }
  },
  fehbCode: (answer) => {
    return {
      autoFocus: false,
      id: 'fehbCode',
      label: 'Select Your FEHB Code',
      placeholder: '',
      inputType: 'select',
      value: answer,
      options: selectData.fehbCode,
    }
  },
  healthInsurancePremium: (answer) => ({
    autoFocus: false,
    id: 'healthInsurancePremium',
    label: 'Healthcare premium per pay period',
    placeholder: '$275.00',
    inputType: 'currencyFloat',
    value: answer,
  }),
  tspContribution: (answer) => ({
    autoFocus: false,
    id: 'tspContribution',
    label: 'TSP Percentage Contribution',
    placeholder: 'Ex. 5.0%',
    inputType: 'percentage',
    value: answer,
  }),
  netTakeHome: (answer) => ({
    id: 'netTakeHome',
    label: (
      <>
        <span>
          What is your net take home per pay period?
          <span style={{ color: 'red' }}> *</span>
          <Tooltip
            TransitionComponent={Fade}
            title="How much you take home in a normal pay period net of all deductions"
            placement="top"
            color="info"
            enterTouchDelay={0}
            leaveTouchDelay={3000}
          >
            <IconButton size="small" color="dark">
              <HelpIcon sx={{ marginTop: '-6px' }} />
            </IconButton>
          </Tooltip>
        </span>
      </>
    ),
    inputType: 'currencyFloat',
    value: answer,
  }),
  // fegliCoverage: (answer) => {
  //   // TODO figure out how to display this one
  // },
  'deduction.fdvv': (answer) => {
    const { deductionTypes } = selectData
    return {
      autoFocus: false,
      id: '',
      label: deductionTypes.find(({ value }) => value === 'fdvv')?.label,
      placeholder: '$112.54',
      inputType: 'currencyFloat',
      value: answer,
    }
  },
  'deduction.fdvd': (answer) => {
    const { deductionTypes } = selectData
    return {
      autoFocus: false,
      id: '',
      label: deductionTypes.find(({ value }) => value === 'fdvd')?.label,
      placeholder: '$112.54',
      inputType: 'currencyFloat',
      value: answer,
    }
  },
}

export const OnboardingUploader = memo(() => {
  const { createAccounts } = useFinances()
  const [queries, setQueries] = useState([]) // replace with useState(fakeQueries) if you want fake data
  const [loading, setLoading] = useState(false)
  const { updateUser, intakeState } = useIntake()

  let stateToValidate = {
    intakeState: {},
    nonUserState: {
      deductions: [],
    },
  }
  if (queries.length) {
    const stuff = queries.forEach(({ questionKey, answer }) => {
      console.log(questionKey)
      if (questionKey.startsWith('deduction')) {
        stateToValidate.nonUserState.deductions.push({
          subType: questionKey.split('.')[1],
          amount: answer,
          type: 'deduction',
        })
      } else {
        stateToValidate.intakeState[questionKey] = questionsToInputs[
          questionKey
        ]
          ? questionsToInputs[questionKey](answer).intakeAttribute
          : answer
      }
    })
  }
  console.log(stateToValidate)
  const handleSubmit = async (dropzoneRef) => {
    setLoading(true)
    setOpen(true)
    console.log(dropzoneRef)
    console.log("i'm HANDLING it, okay?")
    const acceptedFiles = dropzoneRef.getAcceptedFiles()

    const analyzedFile = await standardSizeFileUpload(
      acceptedFiles[0],
      '/api/user_onboarding/analyze_paycheck',
      intakeState?.agency
    )
    const queries = await analyzedFile.json()
    setQueries(queries)
    setTimeout(() => {
      dropzoneRef.removeAllFiles()
      setLoading(false)
    }, 300)
  }

  const handleUpdateFromOCR = async () => {
    await updateUser({ ...intakeState, ...stateToValidate.intakeState })
    await createAccounts(stateToValidate.nonUserState.deductions)
  }

  return (
    <div>
      <Uploader submitHandler={handleSubmit} />
      {/* {loading && <CenteredLoadingContainer />} */}
      <Grid item>
        {queries.length > 0 ? (
          <>
            <h3>Here is what we found. Does this look right?</h3>
            {queries.map(({ question, questionKey, answer }) => {
              return (
                <StyledDiv key={questionKey}>
                  {questionsToInputs[questionKey] ? (
                    <Grid item xs={12} key={questionKey}>
                      {renderMuiFormField(
                        questionsToInputs[questionKey](answer)
                      )}
                    </Grid>
                  ) : (
                    <>
                      <div className="question">
                        <strong>Question: </strong>
                        <p>{question}</p>
                      </div>
                      <div className="answer">
                        <strong>Answer:</strong>
                        <p>{answer}</p>
                      </div>
                    </>
                  )}
                </StyledDiv>
              )
            })}
            <Button onClick={handleUpdateFromOCR}>Looks good!</Button>
          </>
        ) : null}
      </Grid>
    </div>
  )
})

OnboardingUploader.displayName = 'OnboardingUploader'
